import { Button } from '@/components/primitives/Button';
/**
 * BrandFormatterForm — input form for the Brand Formatter (Phase X.3.8).
 * Mirrors EmailDraftForm / AssessmentDraftForm's state + mutation shape.
 *
 * Two fields: a large "paste your text" textarea (required) and an optional
 * document title. onDraftReady passes the structured response + the form
 * snapshot up for the page-level Save Draft payload.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { KeptWorkNotice } from '@/components/KeptWorkNotice';
import { keptWorkKey, useKeptWork } from '@/lib/keptWork';
import { useMutation } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import {
  aiToolsApi,
  type ApiError,
  type BrandFormatDraftInput,
  type BrandFormatDraftResponse,
  type BrandFormatFormInputSnapshot,
} from '@/lib/api';
import { aiGenerateKeyOf } from '@/lib/errorCopy';
import { ErrorNotice } from '@/components/ErrorNotice';
import { BrandFormatModeToggle, type BrandFormatMode } from './BrandFormatModeToggle';
import { TextTooLongNote, isTextTooLong } from './TextTooLongNote';
import { readAiInputMaxChars } from '@/lib/aiInputLimits';
import { aiToolFailureCopy, aiToolFailureOf, type AiToolFailure } from '@/lib/aiTextTooLong';
import { useInvalidateOnCeilingRefusal } from '@/hooks/useInvalidateOnCeilingRefusal';

export interface BrandFormatterFormProps {
  hasDraft?: boolean;
  tenantSlug: string;
  /** Controlled Peace <-> SPICE brand (lifted to the page in C2). */
  mode: BrandFormatMode;
  onModeChange: (mode: BrandFormatMode) => void;
  /** Fired on generation start/finish so the page can show a drafting state. */
  onGeneratingChange?: (generating: boolean) => void;
  onDraftReady: (
    response: BrandFormatDraftResponse,
    formInput: BrandFormatFormInputSnapshot,
  ) => void;
  /** The longest text the formatter takes (characters, after trimming),
   *  read by the page from its tenant config; the factory value until the
   *  page passes one. */
  maxChars?: number;
}

const INPUT_CLS =
  'mt-1.5 block w-full min-h-11 rounded-[12px] border border-sage-400/20 bg-paper px-3.5 py-2.5 text-sm text-ink placeholder:text-sage-300 outline-none transition-[border-color,box-shadow] duration-[var(--dur-base)] ease-quiet focus:border-sage-400 focus:ring-[3px] focus:ring-sage-800/[6%]';
const LABEL_CLS = 'block text-[0.8125rem] font-semibold text-sage-800';

function trimOrUndefined(v: string): string | undefined {
  return v.trim().length > 0 ? v.trim() : undefined;
}

export function BrandFormatterForm({
  hasDraft = false,
  tenantSlug,
  mode,
  onModeChange,
  onGeneratingChange,
  onDraftReady,
  maxChars = readAiInputMaxChars(undefined, 'brand_formatter'),
}: BrandFormatterFormProps): ReactNode {
  const [sourceText, setSourceText] = useState('');
  const [title, setTitle] = useState('');
  // WS-9: the last failure, as a registry key (errorCopy.ts owns the copy)
  // or a refusal for length with the box it names; null = no error banner;
  // an off-registry key renders the honest needs-platform generic.
  const [failure, setFailure] = useState<AiToolFailure | null>(null);
  const onCeilingRefusal = useInvalidateOnCeilingRefusal(tenantSlug);

  // An expired sign-in keeps the pasted text until a format exists; after
  // that the page keeps the result (src/lib/keptWork.ts).
  const kept = useKeptWork<{ sourceText: string; title: string; mode: BrandFormatMode }>(
    keptWorkKey(tenantSlug, 'brand-formatter-source', null),
    () => (hasDraft || (sourceText + title).trim() === '' ? null : { sourceText, title, mode }),
    (k) => {
      setSourceText(k.sourceText);
      setTitle(k.title);
      onModeChange(k.mode);
    },
  );
  useEffect(() => {
    if (hasDraft) kept.clear();
  }, [hasDraft]);

  const snapshot = (): BrandFormatFormInputSnapshot => ({
    title: trimOrUndefined(title),
  });

  const buildInput = (): BrandFormatDraftInput => ({
    source_text: sourceText.trim(),
    title: trimOrUndefined(title),
    // C2: brand-neutral for generation, but sent for API consistency (the
    // download uses it via the saved draft).
    mode,
  });

  // The request and the snapshot the result is filed with are read at the
  // press and travel as the variables.
  const mutation = useMutation({
    mutationFn: (v: { input: BrandFormatDraftInput; snapshot: BrandFormatFormInputSnapshot }) =>
      aiToolsApi.draftBrandFormat(tenantSlug, v.input),
    onSuccess: (res, v) => {
      setFailure(null);
      onDraftReady(res, v.snapshot);
    },
    onError: (e: ApiError, v) => {
      onCeilingRefusal(e);
      // A refusal naming no box reads as the pasted text's.
      setFailure(aiToolFailureOf(e, 'brand_format_generate', 'source_text', v.input, (x) => aiGenerateKeyOf(x) ?? 'unknown'));
    },
  });

  // Elapsed-time reassurance during the streamed generation (mirrors the
  // other tools). Sonnet is quick, but the counter covers a slow run.
  const [elapsed, setElapsed] = useState(0);
  const generating = mutation.isPending;
  useEffect(() => {
    onGeneratingChange?.(generating);
    if (!generating) {
      setElapsed(0);
      return;
    }
    setElapsed(0);
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [generating, onGeneratingChange]);

  const sourceTooLong = isTextTooLong(sourceText, maxChars);
  const canSubmit = sourceText.trim().length > 0 && !sourceTooLong;
  // The title and the text both read the formatter's ceiling.
  const failureCopy = failure === null ? null : aiToolFailureCopy(failure, () => maxChars);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate({ input: buildInput(), snapshot: snapshot() });
      }}
      className="space-y-4"
      data-testid="brand-formatter-form"
    >
      {kept.restored && <KeptWorkNotice onDiscard={kept.discard} />}
      {/* C2: Peace <-> SPICE brand. The mechanical cleanup is identical; this
          only picks which branded .docx the download fills. */}
      <BrandFormatModeToggle mode={mode} onChange={onModeChange} disabled={generating} />

      <div>
        <label htmlFor="bf-title" className={LABEL_CLS}>
          Document title (optional)
        </label>
        <input
          id="bf-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={mutation.isPending}
          placeholder="e.g. Care Plan Summary"
          className={INPUT_CLS}
          data-testid="brand-formatter-title"
        />
      </div>

      <div>
        <label htmlFor="bf-source" className={LABEL_CLS}>
          Paste your text
        </label>
        <p className="text-xs text-ink-soft mt-0.5">
          Notes, a rough draft, bullet fragments, dictation. It gets cleaned up and
          structured into a branded Word document. The wording stays yours; nothing is invented.
        </p>
        <textarea
          id="bf-source"
          value={sourceText}
          onChange={(e) => setSourceText(e.target.value)}
          rows={14}
          disabled={mutation.isPending}
          className={INPUT_CLS}
          data-testid="brand-formatter-source"
        />
        {sourceTooLong && (
          <TextTooLongNote
            surface="brand_format_generate"
            maxChars={maxChars}
            kind="split"
            text={sourceText}
            data-testid="brand-formatter-source-too-long"
          />
        )}
      </div>

      {failureCopy !== null && (
        <ErrorNotice
          surface="brand_format_generate"
          code={failureCopy.key}
          vars={failureCopy.vars}
          data-testid="brand-formatter-error"
        />
      )}

      <Button
        type="submit"
        disabled={!canSubmit || generating}
        variant={hasDraft ? 'ghost' : 'primary'}
        size={hasDraft ? 'sm' : 'md'}
      >
        <Sparkles size={17} aria-hidden="true" />
        {generating
          ? `Formatting your document…${elapsed > 10 ? ` (${elapsed}s)` : ''}`
          : 'Format document'}
      </Button>
    </form>
  );
}
